import React, { useState } from 'react';
import Modal from './Modal';

interface AddCategoryModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: (category: string, options: { persist: boolean; duration: number }) => void;
}

export function AddCategoryModal({ show, onClose, onSubmit }: AddCategoryModalProps) {
    const [category, setCategory] = useState('');
    const [duration, setDuration] = useState(5);
    const [persist, setPersist] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!category.trim()) {
            setError('Category name is required');
            return;
        }

        if (!/^[a-zA-Z0-9-_]+$/.test(category)) {
            setError('Category name can only contain letters, numbers, hyphens, and underscores');
            return;
        }

        onSubmit(category, {
            persist,
            duration: duration * 1000,
        });

        // Reset form
        setCategory('');
        setDuration(5);
        setPersist(false);
        setError('');
        onClose();
    };

    return (
        <Modal
            show={show}
            title="Add Notification Category"
            content={
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Category Name
                        </label>
                        <input
                            type="text"
                            value={category}
                            onChange={(e) => {
                                setCategory(e.target.value);
                                setError('');
                            }}
                            className="input w-full"
                            placeholder="e.g., system-updates"
                        />
                        {error && (
                            <p className="mt-1 text-sm text-error">{error}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Use a descriptive name like "system-updates" or "user-alerts"
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Duration (seconds)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="30"
                            value={duration}
                            onChange={(e) => setDuration(Number(e.target.value))}
                            className="input w-full"
                            disabled={persist}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="persist"
                            checked={persist}
                            onChange={(e) => setPersist(e.target.checked)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <label htmlFor="persist" className="text-sm font-medium">
                            Make notifications persistent
                        </label>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium mb-2">Category Settings Preview</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {persist
                                ? 'Notifications will remain visible until manually dismissed'
                                : `Notifications will automatically dismiss after ${duration} seconds`}
                        </p>
                    </div>
                </div>
            }
            onClose={onClose}
            submitText="Add Category"
            onSubmit={handleSubmit}
        />
    );
}
